import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { VerticalListItem } from "../../services/verticalManagementService";

const dialogBackdropProps = {
  sx: { backgroundColor: "rgba(0, 0, 0, 0.8)" },
};
const dialogPaperProps = {
  sx: {
    width: "600px",
    maxWidth: "calc(100% - 64px)",
    maxHeight: "calc(100% - 64px)",
    borderRadius: "20px",
  },
};
const dialogTitleSx = {
  padding: "40px 40px 0",
  fontSize: "24px",
  fontWeight: 500,
  lineHeight: "29px",
};
const dialogContentSx = {
  padding: "8px 40px 30px",
  "&:first-of-type": { paddingTop: "8px" },
};
const dialogBodySx = {
  color: "#666",
  fontSize: "20px",
  fontWeight: 400,
  lineHeight: "28px",
  marginBottom: "16px",
};

const impactPanelSx = {
  marginBottom: "16px",
};

const impactRowSx = {
  alignItems: "center",
  backgroundColor: "#F0F4F8",
  borderRadius: "8px",
  color: "#000",
  display: "flex",
  fontSize: "20px",
  fontWeight: 400,
  gap: "12px",
  lineHeight: "28px",
  padding: "16px",
};

const impactIconSx = {
  color: "#000",
  flex: "0 0 auto",
  fontSize: "24px",
};

const impactTextSx = {
  color: "#000",
  fontSize: "20px",
  fontWeight: 400,
  lineHeight: "28px",
};
const dialogActionsSx = { padding: "0 40px 40px", gap: "12px" };
const dialogButtonSx = {
  flex: 1,
  height: "56px",
  borderRadius: "8px",
  fontSize: "20px",
  fontWeight: 500,
};
const dialogCancelButtonSx = {
  ...dialogButtonSx,
  backgroundColor: "#C1E7FE",
  color: "#000",
  "&:hover": { backgroundColor: "#A9DCFC" },
};
type DeleteDialogState = { mode: "delete"; vertical: VerticalListItem };

interface VerticalDeleteDialogsProps {
  dialog: DeleteDialogState;
  dialogError: string | null;
  saving: boolean;
  onClose: () => void;
  onDelete: () => void;
}

const VerticalDeleteDialogs: React.FC<VerticalDeleteDialogsProps> = ({
  dialog,
  dialogError,
  saving,
  onClose,
  onDelete,
}) => {
  const assignedCount = dialog.vertical.assignedContentCount;
  const affectedUserCount = dialog.vertical.affectedUserCount ?? 0;

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      BackdropProps={dialogBackdropProps}
      PaperProps={{
        sx: dialogPaperProps.sx,
        ...(saving ? { "aria-busy": true } : {}),
      }}
    >
      <DialogTitle sx={dialogTitleSx}>
        Delete "{dialog.vertical.name}"?
      </DialogTitle>
      <DialogContent sx={dialogContentSx}>
        <Typography sx={dialogBodySx}>
          Its videos lose the tag but stay in their folders. This can’t be
          undone.
        </Typography>
        {assignedCount > 0 && (
          <Box data-testid="vertical-delete-impact" sx={impactPanelSx}>
            <Box data-testid="vertical-delete-video-impact" sx={impactRowSx}>
              <PlayCircleOutlineIcon
                aria-label="Video play icon"
                data-testid="vertical-delete-video-icon"
                sx={impactIconSx}
              />
              <Typography component="span" sx={impactTextSx}>
                {assignedCount === 1
                  ? "1 video loses the tag"
                  : `${assignedCount} videos lose the tag`}
              </Typography>
            </Box>
            <Box
              data-testid="vertical-delete-user-impact"
              sx={{ ...impactRowSx, marginTop: "12px" }}
            >
              <AccountCircleOutlinedIcon
                aria-label="User profile icon"
                data-testid="vertical-delete-access-icon"
                sx={impactIconSx}
              />
              <Typography component="span" sx={impactTextSx}>
                {affectedUserCount === 1
                  ? "1 user loses access to the vertical"
                  : `${affectedUserCount} users lose access to the vertical`}
              </Typography>
            </Box>
          </Box>
        )}
        {saving && (
          <Typography
            role="status"
            aria-live="polite"
            sx={{ ...dialogBodySx, mb: 1 }}
          >
            Deleting vertical…
          </Typography>
        )}
        {dialogError && (
          <Alert severity="error" sx={{ marginTop: "1rem" }}>
            {dialogError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions disableSpacing sx={dialogActionsSx}>
        <Button
          onClick={onClose}
          disabled={saving}
          autoFocus
          sx={dialogCancelButtonSx}
        >
          Cancel
        </Button>
        <Button
          onClick={onDelete}
          color="error"
          variant="contained"
          disabled={saving}
          startIcon={saving && <CircularProgress size={20} />}
          sx={dialogButtonSx}
        >
          Delete Vertical
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VerticalDeleteDialogs;
